import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Sidebar } from '../dashboard_component';
import MyTextEditor from './MyTextEditor';

// Import the centralized API
import { cbtAPI, assetAPI } from '../../api';

const hidden = { display: 'none' };
const visible = { display: 'block' };

// =============================================
//  MODAL COMPONENTS – defined OUTSIDE CbtData
// =============================================

// ---------- CbtConfigModal (with True/False & Fill Blank support) ----------
const CbtConfigModal = ({
    show,
    onHide,
    question,
    setQuestion,
    score,
    setScore,
    max_options_select,
    setmax_options_select,
    question_type_id,
    setQuestionTypeId,
    question_status,
    setQuestionStatus,
    display_sort,
    setDisplaySort,
    cbtConfig,
    db_img_files,
    submitQuestion,
    Questionstatus,
    correctAnswer,
    setCorrectAnswer,
}) => (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" keyboard={false}>
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create New Question
                </h3>
                <button
                    onClick={onHide}
                    className="text-white hover:text-gray-200 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
        <div className="bg-white p-6">
            <form onSubmit={submitQuestion}>
                <div className="mb-6">
                    <label className="block text-gray-700 font-semibold mb-3">Question Content</label>
                    <MyTextEditor
                        initialContent={question}
                        onChange={setQuestion}
                        imageLibrary={db_img_files}
                        // placeholder="Enter your question here..."
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">Question Type</label>
                        <select
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={question_type_id}
                            onChange={(e) => {
                                setQuestionTypeId(e.target.value);
                                setCorrectAnswer('');
                            }}
                        >
                            <option value="objectives">Multiple Choice</option>
                            <option value="theory">Theory/Subjective</option>
                            {/* <option value="true_false">True/False</option> */}
                            {/* <option value="fill_blank">Fill in the Blank</option> */}
                        </select>
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-2">Question Status</label>
                        <select
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={question_status}
                            onChange={(e) => setQuestionStatus(e.target.value)}
                        >
                            <option value="published">Published</option>
                            <option value="draft">Draft</option>
                            <option value="archived">Archived</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-2">
                            Score
                            <span className="text-sm text-gray-500 ml-2">(Default: {cbtConfig.default_score})</span>
                        </label>
                        <input
                            type="number"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={score}
                            onChange={(e) => setScore(e.target.value)}
                            min="0"
                            step="0.5"
                        />
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-2">
                            max_options_select
                            <span className="text-sm text-gray-500 ml-2">(Default: {cbtConfig.max_options_select})</span>
                        </label>
                        <input
                            type="number"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={max_options_select}
                            onChange={(e) => setmax_options_select(e.target.value)}
                            min="1"
                            step="1"
                        />
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-2">Display Order</label>
                        <div className="flex items-center gap-3">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    className="h-4 w-4 text-blue-600"
                                    checked={display_sort === true}
                                    onChange={() => setDisplaySort(true)}
                                />
                                <span className="ml-2">Sequential</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    className="h-4 w-4 text-blue-600"
                                    checked={display_sort === false}
                                    onChange={() => setDisplaySort(false)}
                                />
                                <span className="ml-2">Random</span>
                            </label>
                        </div>
                    </div>

                    {/* Conditional correct answer input for True/False and Fill in the Blank */}
                    {['true_false', 'fill_blank'].includes(question_type_id) && (
                        <div className="md:col-span-2">
                            <label className="block text-gray-700 font-medium mb-2">
                                {question_type_id === 'true_false'
                                    ? 'Correct Answer'
                                    : 'Correct Answer (Fill in the Blank)'}
                            </label>
                            {question_type_id === 'true_false' ? (
                                <div className="flex items-center gap-6">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="trueFalse"
                                            value="true"
                                            checked={correctAnswer === 'true'}
                                            onChange={(e) => setCorrectAnswer(e.target.value)}
                                            className="h-4 w-4 text-blue-600"
                                        />
                                        <span>True</span>
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="trueFalse"
                                            value="false"
                                            checked={correctAnswer === 'false'}
                                            onChange={(e) => setCorrectAnswer(e.target.value)}
                                            className="h-4 w-4 text-blue-600"
                                        />
                                        <span>False</span>
                                    </label>
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={correctAnswer}
                                    onChange={(e) => setCorrectAnswer(e.target.value)}
                                    placeholder="e.g. Paris"
                                />
                            )}
                        </div>
                    )}
                </div>

                <button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={Questionstatus}
                >
                    {Questionstatus ? (
                        <>
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Saving Question...
                        </>
                    ) : (
                        <>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Save Question
                        </>
                    )}
                </button>
            </form>
        </div>
    </Modal>
);

// ---------- ImportQuestionModal (from Code B) ----------
const ImportQuestionModal = ({
    show,
    onHide,
    fileInputRef,
    selectedFile,
    isProcessing,
    uploadStatus,
    dataServer,
    handleFileSelect,
    handleUpload,
    handlePreview,
}) => (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static" keyboard={false}>
        <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-6 rounded-t-lg">
            <h3 className="text-xl font-bold flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                Import Questions from File
            </h3>
        </div>
        <div className="bg-white p-6">
            <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                    Upload Questions File (PDF or DOCX)
                </label>
                <input
                    type="file"
                    ref={fileInputRef}
                    accept=".pdf,.docx"
                    onChange={handleFileSelect}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                    disabled={isProcessing}
                />
                {selectedFile && (
                    <p className="mt-2 text-sm text-gray-600">
                        Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </p>
                )}
            </div>

            {uploadStatus && (
                <div className={`mb-4 p-3 rounded-lg ${uploadStatus.includes('failed') || uploadStatus.includes('error') ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-blue-100 text-blue-700 border border-blue-200'}`}>
                    {uploadStatus}
                </div>
            )}

            <div className="flex justify-end space-x-3">
                <button
                    className="px-5 py-2.5 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                    onClick={onHide}
                    disabled={isProcessing}
                >
                    Cancel
                </button>
                <button
                    className="px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium disabled:bg-green-300 disabled:cursor-not-allowed"
                    onClick={handleUpload}
                    disabled={isProcessing || !selectedFile}
                >
                    {isProcessing ? 'Processing...' : 'Upload & Process'}
                </button>
                {dataServer && dataServer.length > 0 && (
                    <button
                        className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
                        onClick={handlePreview}
                    >
                        Preview ({dataServer.length})
                    </button>
                )}
            </div>
        </div>
    </Modal>
);

// ---------- PreviewQuestionsModal (from Code B) ----------
const PreviewQuestionsModal = ({
    show,
    onHide,
    previewData,
    isProcessing,
    handleConfirmUpload,
}) => (
    <Modal show={show} onHide={onHide} size="xl" centered backdrop="static" keyboard={false}>
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Preview Questions ({previewData?.length || 0})
                </h3>
                <button
                    onClick={onHide}
                    className="text-white hover:text-gray-200 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
        <div className="bg-white p-6" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div className="space-y-6">
                {previewData?.map((question, index) => (
                    <div key={index} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                            <div className="font-semibold text-lg text-gray-800">Question {index + 1}:</div>
                            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                {question.question_type || 'Multiple Choice'}
                            </span>
                        </div>
                        <div
                            className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                            dangerouslySetInnerHTML={{ __html: question.question }}
                        />
                        {question.options && question.options.length > 0 && (
                            <div className="space-y-3">
                                <h5 className="font-medium text-gray-700">Options:</h5>
                                {question.options.map((option, optIndex) => (
                                    <div
                                        key={optIndex}
                                        className={`p-3 rounded-lg border ${option.status || option.is_correct ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium ${option.status || option.is_correct ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'}`}>
                                                {String.fromCharCode(65 + optIndex)}
                                            </span>
                                            <span dangerouslySetInnerHTML={{ __html: option.content || option }} />
                                            {(option.status || option.is_correct) && (
                                                <span className="ml-auto text-green-600 text-sm font-medium">
                                                    ✓ Correct Answer
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {question.correct_answer && (
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <span className="text-sm font-medium text-blue-700">Correct Answer: </span>
                                <span dangerouslySetInnerHTML={{ __html: question.correct_answer }} />
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
        <div className="bg-gray-50 p-6 border-t border-gray-200">
            <div className="flex justify-end gap-3">
                <button
                    className="px-5 py-2.5 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                    onClick={onHide}
                >
                    Cancel
                </button>
                <button
                    className="px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleConfirmUpload}
                    disabled={isProcessing}
                >
                    {isProcessing ? (
                        <>
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Uploading...
                        </>
                    ) : (
                        <>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Confirm & Upload All Questions
                        </>
                    )}
                </button>
            </div>
        </div>
    </Modal>
);

// ---------- ShareCBTModal (from Code B) ----------
const ShareCBTModal = ({ show, onHide, cbt_data_passcode, handleCopyText, handleCopyLink }) => (
    <Modal show={show} onHide={onHide} centered backdrop="static" keyboard={false}>
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    Share CBT
                </h3>
                <button
                    onClick={onHide}
                    className="text-white hover:text-gray-200 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
        <div className="bg-white p-6">
            <div className="mb-6">
                <p className="text-gray-700 mb-4">Share this CBT with others using the following details:</p>
                <div className="space-y-4">
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">CBT ID (Passcode)</label>
                        <div className="flex">
                            <input
                                type="text"
                                readOnly
                                className="flex-1 p-3 border border-gray-300 rounded-l-lg bg-gray-50"
                                value={cbt_data_passcode || ''}
                            />
                            <button
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-r-lg font-medium transition-colors"
                                onClick={handleCopyText}
                            >
                                Copy
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-2">Shareable Link</label>
                        <div className="flex">
                            <input
                                type="text"
                                readOnly
                                className="flex-1 p-3 border border-gray-300 rounded-l-lg bg-gray-50 text-sm"
                                value={`${window.location.origin}/qr_verification?cbt_pass_code=${cbt_data_passcode || ''}`}
                            />
                            <button
                                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-r-lg font-medium transition-colors"
                                onClick={handleCopyLink}
                            >
                                Copy
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-end">
                <button
                    className="px-5 py-2.5 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-lg transition-colors font-medium"
                    onClick={onHide}
                >
                    Close
                </button>
            </div>
        </div>
    </Modal>
);

// ---------- DeleteConfirmationModal (from Code B) ----------
const DeleteConfirmationModal = ({ show, onHide, currentQuestion, deleteQuestion }) => (
    <Modal show={show} onHide={onHide} centered backdrop="static" keyboard={false}>
        <div className="bg-gradient-to-r from-red-600 to-pink-600 text-white p-6 rounded-t-lg">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Confirm Delete
                </h3>
                <button
                    onClick={onHide}
                    className="text-white hover:text-gray-200 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
        <div className="bg-white p-6">
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-r-lg">
                <p className="font-medium">Are you sure you want to delete this question? This action cannot be undone.</p>
            </div>

            {currentQuestion && (
                <div className="border border-gray-200 rounded-xl p-4 mb-6 bg-gray-50">
                    <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: currentQuestion.question }} />
                    <div className="mt-3 flex items-center gap-3">
                        <span className="bg-blue-100 text-blue-800 px-2.5 py-1 rounded-full text-xs font-medium">
                            Score: {currentQuestion.score}
                        </span>
                        <span className="bg-purple-100 text-purple-800 px-2.5 py-1 rounded-full text-xs font-medium capitalize">
                            {currentQuestion.question_type}
                        </span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${currentQuestion.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {currentQuestion.status}
                        </span>
                    </div>
                </div>
            )}

            <div className="flex justify-end gap-3">
                <button
                    className="px-5 py-2.5 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-xl transition-colors font-medium"
                    onClick={onHide}
                >
                    Cancel
                </button>
                <button
                    className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors font-medium flex items-center gap-2"
                    onClick={() => {
                        if (currentQuestion) {
                            deleteQuestion(currentQuestion);
                        }
                    }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Permanently
                </button>
            </div>
        </div>
    </Modal>
);

// =============================================
//  MAIN COMPONENT
// =============================================

const CbtData = () => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [question, setQuestion] = useState("");
    const [score, setScore] = useState(1);
    const [max_options_select, setmax_options_select] = useState(1);
    const [question_type_id, setQuestionTypeId] = useState("objectives");
    const [sort, setSort] = useState(1);
    const [dbQuestions, setDbQuestions] = useState([]);

    const url_param = new URLSearchParams(window.location.search);
    const cbt_data_passcode = url_param.get("cbt_data_id");

    const [question_status, setQuestionStatus] = useState("draft");
    const [display_sort, setDisplaySort] = useState(true);
    const [button_active, setButtonActive] = useState(visible);
    const [button_effect, setButtonEffect] = useState(hidden);
    const [cbtData, setCbtData] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [importQuestionModal, setImportQuestionShareModal] = useState(false);
    const [exportQuestionModal, setExportQuestionShareModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const navigate = useNavigate();

    const [db_img_files, set_db_img_file] = useState([]);
    const [loading, setLoading] = useState(false);
    const [Questionstatus, setQuestionstatus] = useState(false);
    const [dataExport, setDataExport] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportStatus, setExportStatus] = useState('');

    // State for correct answer (True/False and Fill in the Blank)
    const [correctAnswer, setCorrectAnswer] = useState('');

    const handleToggle = () => setIsCollapsed(!isCollapsed);

    // File upload states
    const [selectedFile, setSelectedFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('');
    const [passcode, setPasscode] = useState(cbt_data_passcode);
    const [dataServer, setDataServer] = useState([]);
    const fileInputRef = useRef(null);
    const [previewData, setPreviewData] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    // CBT Configuration States
    const [cbtConfig, setCbtConfig] = useState({
        cbt_name: "",
        attempt_limit: 1,
        default_score: 1,
        duration: "",
        max_questions: "",
        random_questions: false,
        status: 'draft',
        max_options_select: 1
    });

    const handlePreview = () => {
        setShowPreviewModal(true);
    };

    const handleConfirmUpload = () => {
        bulkCreateQuestions();
        setShowPreviewModal(false);
        setImportQuestionShareModal(false);
    };

    // Function to create multiple questions in bulk
    const bulkCreateQuestions = async () => {
        if (!dataServer.length) {
            toast.error("No questions to upload");
            return;
        }

        setIsProcessing(true);

        try {
            const formattedQuestions = dataServer.map(q => ({
                question: q.question,
                question_type: q.question_type || 'objectives',
                score: q.score || cbtConfig.default_score,
                sort: q.sort || 1,
                status: question_status,
                options: (q.options || []).map(opt => ({
                    content: typeof opt === 'string' ? opt : opt.content,
                    status: opt.is_correct || opt.status || false
                })),
                correct_answer: q.correct_answer || (q.options?.length > 0 ? q.options[0] : '')
            }));

            const response = await cbtAPI.bulkCreateQuestions(cbt_data_passcode, formattedQuestions);

            if (response.success && response.data) {
                toast.success(`Successfully uploaded ${response.data.question_count || formattedQuestions.length} questions`);
                refreshQuestions();
                setDataServer([]);
            } else {
                toast.error(response.error || 'Failed to upload questions');
            }

            setImportQuestionShareModal(false);
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.data?.detail || error.message || 'Upload failed. Please try again...');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file && (file.type === 'application/pdf' ||
            file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
            setSelectedFile(file);
            setUploadStatus('');
        } else {
            setUploadStatus('Please select a PDF or DOCX file');
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setUploadStatus('Please select a file first');
            return;
        }

        if (!cbt_data_passcode) {
            setUploadStatus('Please enter the passcode');
            return;
        }

        setIsProcessing(true);
        setUploadStatus('Processing your file...');

        try {
            const response = await cbtAPI.uploadQuestionsFile(cbt_data_passcode, selectedFile);

            if (response.data) {
                setUploadStatus('File uploaded successfully! Questions are being processed.');
                const questions = response.data.questions;
                setDataServer(questions);
                setPreviewData(questions);
                toast.success('File processed successfully! Preview questions before uploading.');
            } else {
                setUploadStatus('Upload completed but no data returned');
            }
        } catch (error) {
            console.error('Upload error:', error);
            setUploadStatus(error.response?.data?.detail || 'Upload failed');
            toast.error(error.response?.data?.detail || 'Upload failed. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    // Helper function to parse uploaded questions (kept for reference)
    const parseQuestionsFromResponse = (data) => {
        if (!data || !Array.isArray(data)) return [];
        return data.map((q, index) => ({
            question: q.question || q.content || '',
            question_type: q.question_type || 'objectives',
            score: q.score || 1,
            sort: index + 1,
            options: q.options || [],
            correct_answer: q.correct_answer || q.answer || '',
            explanation: q.explanation || ''
        }));
    };

    // Enhanced Export function using centralized API
    const handleExport = async () => {
        setExportLoading(true);
        setExportStatus('Processing questions for export...');

        try {
            const response = await cbtAPI.getQuestions(cbt_data_passcode);

            if (response.data && response.data.data && response.data.data.length > 0) {
                const exportDataNew = response.data.data.map(q => ({
                    question: q.question,
                    question_type: q.question_type,
                    score: q.score,
                    sort: q.sort,
                    status: q.status,
                    options: q.options || [],
                    correct_answer: q.correct_answer,
                    explanation: q.explanation
                }));

                const exportData = response.data.data.map((q, qIndex) => ({
                    id: q.cbt_question_id,
                    question_text: q.question,
                    question_type: q.question_type === 'multiple_choice' ? 'mcq' : q.question_type,
                    marks: q.score,
                    order_index: q.sort ?? qIndex,
                    options: (q.answers || []).map((a, index) => ({
                        label: String.fromCharCode(97 + index), // a, b, c, d
                        option: a.options,
                        status: a.option_status,
                        answer_id: a.answer_id
                    })),
                    correct_answer: q.correct_answer
                }));

                setDataExport(exportData);

                const jsonStr = JSON.stringify(exportData, null, 2);
                const blob = new Blob([jsonStr], { type: "application/json" });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `${cbt_data_passcode}_questions.json`;
                link.click();

                setExportStatus('Export completed successfully!');
                toast.success('Questions exported successfully!');
            } else {
                setExportStatus('No data available for export');
                toast.info('No questions available for export');
            }
        } catch (error) {
            console.error('Export error:', error);
            setExportStatus('Export failed. Please try again.');
            toast.error('Export failed. Please try again.');
        } finally {
            setExportLoading(false);
            setTimeout(() => setExportStatus(''), 3000);
        }
    };

    // Fetch images using centralized API
    const refresh_img = async () => {
        try {
            const response = await assetAPI.getImageLibrary();
            set_db_img_file(response.data || []);
        } catch (error) {
            toast.error('Failed to load images');
        }
    };

    // Fetch CBT details using centralized API
    const fetchCbtDetails = async () => {
        try {
            const response = await cbtAPI.getCBTDetails(cbt_data_passcode);
            if (response.data) {
                setCbtData(response.data);
                setCbtConfig({
                    cbt_name: response.data.cbt_name || "",
                    attempt_limit: response.data.attempt_limit || 1,
                    default_score: response.data.default_score || 1,
                    duration: response.data.duration || "",
                    max_questions: response.data.max_questions || "",
                    random_questions: response.data.random_questions || false,
                    status: response.data.status || 'draft',
                    max_options_select: response.data.max_options_select || 1
                });
            }
        } catch (error) {
            console.error('Failed to fetch CBT details:', error);
        }
    };

    // Fetch questions using centralized API
    const fetchQuestions = async () => {
        try {
            const response = await cbtAPI.getQuestions(cbt_data_passcode);
            if (response.data && response.data.data) {
                setDbQuestions(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch questions:', error);
        }
    };

    useEffect(() => {
        fetchCbtDetails();
        refresh_img();
        fetchQuestions();
    }, []);

    const refreshQuestions = () => {
        fetchQuestions();
    };

    // Enhanced question submission with proper data formatting
    const submitQuestion = async (event) => {
        event.preventDefault();
        if (!question.trim()) {
            toast.error("CBT question cannot be empty");
            return;
        }

        // Validation for True/False
        if (question_type_id === 'true_false' && !correctAnswer) {
            toast.error("Please select the correct answer (True/False)");
            return;
        }
        // Validation for Fill in the Blank
        if (question_type_id === 'fill_blank' && !correctAnswer.trim()) {
            toast.error("Please enter the correct answer for the blank");
            return;
        }

        setQuestionstatus(true);

        const questionTypeMap = {
            "objectives": "multiple_choice",
            "theory": "essay",
            "true_false": "true_false",
            "fill_blank": "fill_blank"
        };

        const questionData = {
            cbt_data_id: cbt_data_passcode,
            question: question,
            question_type: questionTypeMap[question_type_id] || question_type_id,
            score: parseFloat(score),
            sort: parseInt(sort),
            status: question_status,
            display_sort: display_sort,
            options: [],
            correct_answer: correctAnswer,
            explanation: ""
        };

        try {
            const response = await cbtAPI.createQuestion(cbt_data_passcode, questionData);

            if (response.success || response.data) {
                setShowCreateModal(false);
                refreshQuestions();
                // Reset form
                setQuestion("");
                setScore(1);
                setSort(1);
                setCorrectAnswer("");
                setQuestionTypeId("objectives");
                toast.success('Question created successfully!');
            } else {
                toast.error(response.error || response.message || 'Failed to create question');
            }
        } catch (error) {
            console.error('Create question error:', error);

            if (error.response?.data?.detail) {
                const errorDetails = error.response.data.detail;
                if (Array.isArray(errorDetails)) {
                    errorDetails.forEach(err => {
                        toast.error(`${err.loc?.join('.')}: ${err.msg}`);
                    });
                } else {
                    toast.error(errorDetails || 'Failed to create question');
                }
            } else {
                toast.error(error.message || 'Failed to create question');
            }
        } finally {
            setQuestionstatus(false);
        }
    };

    const handleCopyText = () => {
        navigator.clipboard.writeText(cbt_data_passcode || '')
            .then(() => toast.success("CBT ID copied to clipboard!"))
            .catch(() => toast.error("Failed to copy text"));
    };

    const handleCopyLink = () => {
        const link = `${window.location.origin}/qr_verification?cbt_pass_code=${cbt_data_passcode || ''}`;
        navigator.clipboard.writeText(link)
            .then(() => toast.success("Link copied to clipboard!"))
            .catch(() => toast.error("Failed to copy link"));
    };

    // Enhanced delete question function
    const deleteQuestion = async (question) => {
        setButtonActive(hidden);
        setButtonEffect(visible);

        try {
            await cbtAPI.deleteQuestion(cbt_data_passcode, question.cbt_question_id);
            toast.success('Question deleted successfully!');
            setShowDeleteModal(false);
            refreshQuestions();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete question');
        } finally {
            setButtonActive(visible);
            setButtonEffect(hidden);
        }
    };

    return (
        <div className="flex min-h-screen bg-gray-50">
            <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />

            <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-5 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    {/* Header Section */}
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-xl overflow-hidden mb-8">
                        <div className="p-6 md:p-8 text-white">
                            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                                <div className="flex-1">
                                    <h1 className="text-2xl md:text-3xl font-bold mb-2 flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                        </svg>
                                        {cbtData?.cbt_name || 'CBT Management'}
                                    </h1>
                                    <p className="text-blue-100 text-sm md:text-base">
                                        {cbtData?.description || 'Manage your questions and exam settings'}
                                        <span className="ml-4 text-sm">
                                            ID: <code className="bg-white bg-opacity-20 px-2 py-1 rounded">{cbt_data_passcode}</code>
                                        </span>
                                    </p>
                                </div>
                                <button
                                    className="bg-white text-blue-600 hover:bg-blue-50 px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all duration-200 hover:scale-105 shadow-lg"
                                    onClick={() => setShowCreateModal(true)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                    </svg>
                                    Create Question
                                </button>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="bg-white bg-opacity-10 p-6">
                            <div className="flex flex-wrap gap-3">
                                <button
                                    className="flex items-center gap-2 border border-white text-white  hover:text-blue-600 px-4 py-2 rounded-xl transition-all duration-200"
                                    onClick={() => setShowShareModal(true)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                                    </svg>
                                    Share CBT
                                </button>

                                <a
                                    href={`/cbt_creator/test_result/${cbt_data_passcode}`}
                                    className="flex items-center gap-2 border border-white text-white  hover:text-green-900 px-4 py-2 rounded-xl transition-all duration-200"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-4-4a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    View Results
                                </a>

                                <button
                                    className="flex items-center gap-2 border border-white text-white  hover:text-indigo-600 px-4 py-2 rounded-xl transition-all duration-200"
                                    onClick={() => navigate(`/qr_verification?cbt_pass_code=${cbt_data_passcode}`)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 2V5h1v1H5zm-2 7a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zm2 2v-1h1v1H5zm8-12a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V4a1 1 0 00-1-1h-3zm1 2v1h1V5h-1z" clipRule="evenodd" />
                                        <path d="M11 4a1 1 0 10-2 0v1a1 1 0 002 0V4zM10 7a1 1 0 011 1v1h2a1 1 0 110 2h-3a1 1 0 01-1-1V8a1 1 0 011-1zM16 9a1 1 0 100 2 1 1 0 000-2zM9 13a1 1 0 100-2H7a1 1 0 100 2h2zM13 13a1 1 0 110-2h2a1 1 0 110 2h-2z" />
                                    </svg>
                                    QR Code
                                </button>

                                <button
                                    className="flex items-center gap-2 border border-white text-white  hover:text-purple-600 px-4 py-2 rounded-xl transition-all duration-200"
                                    onClick={() => navigate(`/cbt_creator/test_data_upload/${cbt_data_passcode}`)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6h.1a5 5 0 011 9.9M12 12v6m0 0l-3-3m3 3l3-3" />
                                    </svg>
                                    Student CSV/Excel Import
                                </button>

                                <button
                                    className="flex items-center gap-2 border border-white text-white  hover:text-green-600 px-4 py-2 rounded-xl transition-all duration-200"
                                    onClick={() => setImportQuestionShareModal(true)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6h.1a5 5 0 011 9.9M12 8v8m0 0l-3-3m3 3l3-3" />
                                    </svg>
                                    Import Questions
                                </button>

                                <button
                                    className="flex items-center gap-2 border border-white text-white  hover:text-orange-600 px-4 py-2 rounded-xl transition-all duration-200"
                                    onClick={handleExport}
                                    disabled={exportLoading}
                                >
                                    {exportLoading ? (
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    )}
                                    {exportLoading ? 'Exporting...' : 'Export Questions'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Question Navigation */}
                    {dbQuestions.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
                            <div className="bg-gray-50 p-6 border-b border-gray-100">
                                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                    </svg>
                                    Question Navigation ({dbQuestions.length} questions)
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="flex flex-wrap gap-2">
                                    {dbQuestions.map((_, index) => (
                                        <a
                                            key={index}
                                            className="bg-blue-100 hover:bg-blue-200 text-blue-800 w-10 h-10 flex items-center justify-center rounded-lg font-medium transition-colors hover:scale-105"
                                            href={`#question${index}`}
                                        >
                                            {index + 1}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Questions List */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="bg-gray-50 p-6 border-b border-gray-100">
                            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Questions ({dbQuestions.length})
                            </h2>
                        </div>
                        <div className="p-6">
                            {dbQuestions.length > 0 ? (
                                dbQuestions.map((question, index) => (
                                    <QuestionLoop
                                        key={question.cbt_question_id}
                                        i={index}
                                        question={question}
                                        cbt_pass_code={cbt_data_passcode}
                                        onDeleteClick={(q) => {
                                            setCurrentQuestion(q);
                                            setShowDeleteModal(true);
                                        }}
                                    />
                                ))
                            ) : (
                                <div className="text-center py-16">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24 mx-auto text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                    <p className="text-gray-500 text-lg mt-6 mb-4">No questions created yet</p>
                                    <p className="text-gray-400 mb-8">Start by creating your first question or import questions from a file</p>
                                    <div className="flex gap-4 justify-center">
                                        <button
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all duration-200 hover:scale-105"
                                            onClick={() => setShowCreateModal(true)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                            </svg>
                                            Create First Question
                                        </button>
                                        <button
                                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all duration-200 hover:scale-105"
                                            onClick={() => setImportQuestionShareModal(true)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                                            </svg>
                                            Import Questions
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* MODALS */}
            <CbtConfigModal
                show={showCreateModal}
                onHide={() => {
                    setShowCreateModal(false);
                    setCorrectAnswer('');
                    setQuestion('');
                    setScore(1);
                    setmax_options_select(1);
                    setQuestionTypeId('objectives');
                    setQuestionStatus('draft');
                    setDisplaySort(true);
                }}
                question={question}
                setQuestion={setQuestion}
                score={score}
                setScore={setScore}
                max_options_select={max_options_select}
                setmax_options_select={setmax_options_select}
                question_type_id={question_type_id}
                setQuestionTypeId={setQuestionTypeId}
                question_status={question_status}
                setQuestionStatus={setQuestionStatus}
                display_sort={display_sort}
                setDisplaySort={setDisplaySort}
                cbtConfig={cbtConfig}
                db_img_files={db_img_files}
                submitQuestion={submitQuestion}
                Questionstatus={Questionstatus}
                correctAnswer={correctAnswer}
                setCorrectAnswer={setCorrectAnswer}
            />

            <ImportQuestionModal
                show={importQuestionModal}
                onHide={() => {
                    setImportQuestionShareModal(false);
                    setDataServer([]);
                    setUploadStatus('');
                    setSelectedFile(null);
                }}
                fileInputRef={fileInputRef}
                selectedFile={selectedFile}
                isProcessing={isProcessing}
                uploadStatus={uploadStatus}
                dataServer={dataServer}
                handleFileSelect={handleFileSelect}
                handleUpload={handleUpload}
                handlePreview={handlePreview}
            />

            <PreviewQuestionsModal
                show={showPreviewModal}
                onHide={() => setShowPreviewModal(false)}
                previewData={previewData}
                isProcessing={isProcessing}
                handleConfirmUpload={handleConfirmUpload}
            />

            <ShareCBTModal
                show={showShareModal}
                onHide={() => setShowShareModal(false)}
                cbt_data_passcode={cbt_data_passcode}
                handleCopyText={handleCopyText}
                handleCopyLink={handleCopyLink}
            />

            <DeleteConfirmationModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                currentQuestion={currentQuestion}
                deleteQuestion={deleteQuestion}
            />

            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

// =============================================
//  QUESTION LOOP – Enhanced with True/False & Fill Blank display
// =============================================
const QuestionLoop = forwardRef(({ i, question, onDeleteClick, cbt_pass_code }, ref) => {
    const navigate = useNavigate();

    return (
        <div
            className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden mb-6 hover:shadow-md transition-all duration-200"
            id={`question${i}`}
            ref={ref}
        >
            <div className="bg-gradient-to-r from-gray-50 to-white p-6">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    <div className="flex items-center gap-4">
                        <div className="bg-blue-500 text-white w-10 h-10 flex items-center justify-center rounded-xl font-bold shadow-sm">
                            {i + 1}
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">Question {i + 1}</h3>
                            <div className="flex items-center gap-3 mt-1">
                                <span className="bg-blue-100 text-blue-800 px-2.5 py-1 rounded-full text-xs font-medium">
                                    Score: {question.score}
                                </span>
                                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${question.status === 'published'
                                        ? 'bg-green-100 text-green-800'
                                        : question.status === 'draft'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-gray-100 text-gray-800'
                                    }`}>
                                    {question.status || 'draft'}
                                </span>
                                <span className="bg-purple-100 text-purple-800 px-2.5 py-1 rounded-full text-xs font-medium capitalize">
                                    {question.question_type === 'multiple_choice' ? 'Multiple Choice' :
                                        question.question_type === 'essay' ? 'Essay' :
                                            question.question_type === 'true_false' ? 'True/False' :
                                                question.question_type === 'fill_blank' ? 'Fill in Blank' :
                                                    question.question_type}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 px-4 py-2 border border-blue-600 rounded-xl transition-all duration-200 hover:bg-blue-50"
                            onClick={() => navigate(`/cbt_creator/cbt_question_edit?cbt_data_id=${cbt_pass_code}&cbt_question_id=${question.cbt_question_id}`)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                        </button>
                        <button
                            className="flex items-center gap-2 text-red-600 hover:text-red-800 px-4 py-2 border border-red-600 rounded-xl transition-all duration-200 hover:bg-red-50"
                            onClick={() => onDeleteClick(question)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                        </button>
                    </div>
                </div>
            </div>
            <div className="p-6">
                <div className="prose max-w-none mb-4" dangerouslySetInnerHTML={{ __html: question.question }} />

                {/* Display options if available */}
                {question.answers && question.answers.length > 0 && (
                    <div className="mt-4">
                        <h4 className="font-medium text-gray-700 mb-2">Options:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {question.answers.map((answer, idx) => (
                                <div
                                    key={answer.answer_id}
                                    className={`p-3 rounded-lg border ${answer.option_status === 'correct' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                                        }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className={`w-6 h-6 flex items-center justify-center rounded text-sm font-medium ${answer.option_status === 'correct' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'
                                            }`}>
                                            {String.fromCharCode(65 + idx)}
                                        </span>
                                        <span dangerouslySetInnerHTML={{ __html: answer.options }} />
                                        {answer.option_status === 'correct' && (
                                            <span className="ml-auto text-green-600 text-sm">
                                                ✓ Correct
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Display correct_answer for True/False and Fill in the Blank */}
                {question.question_type === 'true_false' && question.correct_answer && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <span className="text-sm font-medium text-blue-700">Correct Answer: </span>
                        <span className="capitalize">{question.correct_answer === 'true' ? 'True' : 'False'}</span>
                    </div>
                )}
                {question.question_type === 'fill_blank' && question.correct_answer && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <span className="text-sm font-medium text-blue-700">Correct Answer: </span>
                        <span>{question.correct_answer}</span>
                    </div>
                )}
            </div>
        </div>
    );
});

export { CbtData };